import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const MILESTONES = [25, 50, 75, 100];
const STORAGE_KEY = 'aura.dashboard.milestones';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const loadSeen = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const saveSeen = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
};

/**
 * One tasteful celebration when a real milestone or streak extension occurs — never on app open alone.
 */
const DashboardMilestoneDelight = ({ visions = [], streak }) => {
  const [celebration, setCelebration] = useState(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current || prefersReducedMotion()) return;

    const seen = loadSeen();
    let trigger = null;

    for (const v of visions) {
      const id = v.vision_id;
      const pct = Number(v.progress_pct) || 0;
      const prev = Number(seen[`vision_${id}`]) || 0;
      const crossed = MILESTONES.find((m) => prev < m && pct >= m);
      if (crossed) {
        trigger = {
          type: 'milestone',
          message: `${v.name || 'Your Vision'} — ${crossed}% there`,
        };
        seen[`vision_${id}`] = pct;
        break;
      }
      seen[`vision_${id}`] = pct;
    }

    const streakCount = Number(streak?.streak) || 0;
    const prevStreak = Number(seen.streak) || 0;
    if (!trigger && streakCount > prevStreak && streakCount > 1) {
      trigger = {
        type: 'streak',
        message: `${streakCount}-day streak — keep it going`,
      };
    }
    seen.streak = streakCount;
    saveSeen(seen);

    if (trigger) {
      firedRef.current = true;
      setCelebration(trigger);
      const t = setTimeout(() => setCelebration(null), 2800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [visions, streak]);

  return (
    <AnimatePresence>
      {celebration && (
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          className="fixed inset-x-0 top-20 z-50 flex justify-center pointer-events-none px-4"
          role="status"
          aria-live="polite"
        >
          <div className="dashboard-celebration-glow pointer-events-auto px-6 py-4 rounded-xl border border-pine/30 bg-bone-raised/95 shadow-elevated backdrop-blur-sm">
            <p className="font-display text-h3 text-pine text-center">{celebration.message}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DashboardMilestoneDelight;
